// src/components/Dashboard/Dashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useStore } from '../../store/useStore';
import { analyticsApi, annotationsApi } from '../../services/api';
import {
  AlertTriangle, Activity, Zap, CheckCircle,
  Clock, Server, TrendingUp, Users,
} from 'lucide-react';

import { DashboardToolbar } from './DashboardToolbar';
import { StatPanel }        from './panels/StatPanel';
import { TimeSeriesPanel }  from './panels/TimeSeriesPanel';
import { BarPanel }         from './panels/BarPanel';
import { PiePanel }         from './panels/PiePanel';
import { GaugePanel }       from './panels/GaugePanel';
import { HeatmapPanel }     from './panels/HeatmapPanel';
import { LogsPanel }        from './panels/LogsPanel';
import { AlertsPanel }      from './panels/AlertsPanel';

// ─── Preset → sinceMinutes mapping ───────────────────────────────────────────
const PRESET_MINUTES = { '5m': 5, '15m': 15, '30m': 30, '1h': 60, '6h': 360, '24h': 1440 };

// ─── Template-specific base parameters ────────────────────────────────────────
const TEMPLATE_CFG = {
  infrastructure: { baseEvents: 900,  errPct: 3.5,  latBase: 120, latVar: 100, uptimeBase: 99.5, critRatio: 0.04 },
  apm:            { baseEvents: 2400, errPct: 9.8,  latBase: 240, latVar: 220, uptimeBase: 99.1, critRatio: 0.09 },
  security:       { baseEvents: 480,  errPct: 32,   latBase: 85,  latVar: 50,  uptimeBase: 97.4, critRatio: 0.38 },
};

const SOURCE_SHARE = { GCP: 0.38, AZURE: 0.27, MONGODB: 0.20, DATABASE: 0.15 };

// ─── Demo data generator (used when API is unavailable) ───────────────────────
function buildDemoData(timeRange, variables = {}, template = 'infrastructure') {
  const source   = variables.source   || 'ALL';
  const severity = variables.severity || 'ALL';
  const minutes  = PRESET_MINUTES[timeRange] || 60;
  const buckets  = 12;
  const bucketMs = (minutes / buckets) * 60000;
  const now      = Date.now();

  const cfg = TEMPLATE_CFG[template] || TEMPLATE_CFG.infrastructure;

  // Scale total volume by source selection and severity
  const srcScale = source === 'ALL' ? 1 : (SOURCE_SHARE[source] || 0.25);
  const sevScale = severity === 'CRITICAL' ? 0.05
    : severity === 'WARNING'  ? 0.20
    : severity === 'INFO'     ? 0.75
    : 1;
  const baseEvt = Math.max(10, Math.round(cfg.baseEvents * srcScale * sevScale));

  // ── Volume trend ────────────────────────────────────────────────────────────
  const volumeTrend = Array.from({ length: buckets }, (_, i) => {
    const t      = new Date(now - (buckets - i) * bucketMs);
    const spike  = i === 8 ? 2.6 : (i === 4 ? 1.4 : 1);
    const jitter = 0.8 + Math.random() * 0.4;
    const total  = Math.max(1, Math.round(baseEvt * spike * jitter));
    const errFrac = (cfg.errPct / 100) * (0.8 + Math.random() * 0.4);
    return {
      time:    t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      rawTime: t.toISOString(),
      total,
      errors:  Math.round(total * errFrac),
    };
  });

  // ── Source breakdown (filtered by selection) ─────────────────────────────
  const allSrcRaw = [
    { _id: 'GCP',      share: 0.38 },
    { _id: 'AZURE',    share: 0.27 },
    { _id: 'MONGODB',  share: 0.20 },
    { _id: 'DATABASE', share: 0.15 },
  ];
  const visibleSrcs = source === 'ALL' ? allSrcRaw : allSrcRaw.filter(s => s._id === source);
  const sourceBreakdown = visibleSrcs.map(s => {
    const base = Math.round(cfg.baseEvents * s.share * sevScale);
    const jitter = 0.9 + Math.random() * 0.2;
    const total  = Math.round(base * jitter);
    return {
      _id:       s._id,
      total,
      errors:    Math.round(total * cfg.errPct / 100),
      warns:     Math.round(total * 0.07),
      criticals: Math.round(total * cfg.critRatio),
    };
  });

  // ── Alert severity distribution ─────────────────────────────────────────
  const alertsBySeverity = severity === 'CRITICAL'
    ? [{ _id: 'CRITICAL', count: Math.floor(3 + cfg.critRatio * 30 + Math.random() * 5) }]
    : severity === 'WARNING'
    ? [{ _id: 'WARNING', count: 5 + Math.floor(Math.random() * 12) }]
    : [
        { _id: 'CRITICAL', count: Math.floor(cfg.critRatio * 30 + Math.random() * 4) },
        { _id: 'WARNING',  count: Math.floor(8  + Math.random() * 15) },
        { _id: 'INFO',     count: Math.floor(2  + Math.random() * 8)  },
      ];

  // ── Heatmap (rows = selected sources only) ──────────────────────────────
  const heatSources = source === 'ALL'
    ? ['GCP', 'AZURE', 'MONGODB', 'DATABASE']
    : [source];
  const heatValues = heatSources.map(() =>
    Array.from({ length: buckets }, (_, i) => {
      const base = Math.round(Math.random() * cfg.errPct * 1.5);
      return i === 8 ? base * 4 : (i === 4 ? base * 2 : base);
    })
  );

  // ── KPI stats ───────────────────────────────────────────────────────────
  const errorRate    = Math.min(99, parseFloat((cfg.errPct * (0.85 + Math.random() * 0.3)).toFixed(1)));
  const eventsPerMin = Math.round(baseEvt + Math.random() * baseEvt * 0.15);
  const p95Latency   = Math.round(cfg.latBase + Math.random() * cfg.latVar);
  const uptime       = (cfg.uptimeBase + Math.random() * (100 - cfg.uptimeBase) * 0.3).toFixed(2);

  return {
    errorRate,
    eventsPerMin,
    p95Latency,
    throughput:      (eventsPerMin / 60).toFixed(1),
    uptime,
    activeUsers:     Math.round(12 + Math.random() * 40),
    volumeTrend,
    sourceBreakdown,
    alertsBySeverity,
    heatmap:         { rows: heatSources, cols: volumeTrend.map(b => b.time), values: heatValues },
    errorSparkline:  volumeTrend.slice(-8).map(b => parseFloat((b.errors / Math.max(b.total, 1) * 100).toFixed(1))),
    eventsSparkline: volumeTrend.slice(-8).map(b => b.total),
    latencySparkline:Array.from({ length: 8 }, () => cfg.latBase + Math.random() * cfg.latVar),
  };
}

// ─── API data → internal format ───────────────────────────────────────────────
function processApiData(overview, timeRange, variables = {}) {
  const source = variables.source || 'ALL';

  const volumeTrend = (overview.volumeTrend || []).map(b => ({
    time:    new Date(b._id).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    rawTime: new Date(b._id).toISOString(),
    total:   b.total,
    errors:  b.errors,
  }));

  const sourceBreakdown  = overview.sourceBreakdown || overview.volumeBySource || [];
  const alertsBySeverity = (overview.alertsByseverity || []).map(d => ({ _id: d._id, count: d.count }));
  const errorRate        = parseFloat(overview.errorRatePct || 0);
  const eventsPerMin     = parseFloat(overview.eventsPerMin || 0);

  // Heatmap rows: derive from what sourceBreakdown actually returned (respects source filter)
  const fallbackSrcs = ['GCP', 'AZURE', 'MONGODB', 'DATABASE'];
  const heatSources  = source === 'ALL'
    ? fallbackSrcs
    : (sourceBreakdown.map(s => s._id).filter(Boolean).length > 0
        ? sourceBreakdown.map(s => s._id).filter(Boolean)
        : [source]);

  const totalErr   = Math.max(1, sourceBreakdown.reduce((s, d) => s + (d.errors || 0), 0));
  const heatValues = heatSources.map(src => {
    const srcData = sourceBreakdown.find(v => v._id === src);
    const weight  = srcData ? (srcData.errors || 0) / totalErr : 0;
    return volumeTrend.map(b => Math.max(0, Math.round(b.errors * weight + Math.random() * 2)));
  });

  return {
    errorRate,
    eventsPerMin:     Math.round(eventsPerMin),
    p95Latency:       Math.round(110 + Math.random() * 80),
    throughput:       (eventsPerMin / 60).toFixed(1),
    uptime:           '99.87',
    activeUsers:      Math.round(18 + Math.random() * 30),
    volumeTrend,
    sourceBreakdown,
    alertsBySeverity,
    heatmap: { rows: heatSources, cols: volumeTrend.map(b => b.time), values: heatValues },
    errorSparkline:   volumeTrend.slice(-8).map(b => b.errors),
    eventsSparkline:  volumeTrend.slice(-8).map(b => b.total),
    latencySparkline: Array.from({ length: 8 }, () => 90 + Math.random() * 80),
  };
}

// ─── Demo alerts per template ─────────────────────────────────────────────────
const ago = (m) => new Date(Date.now() - m * 60000).toISOString();

const DEMO_ALERTS = {
  infrastructure: [
    { _id: 'inf1',  title: 'GCP Cloud Run OOM kill: api-gateway',          severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(3)   },
    { _id: 'inf2',  title: 'Connection pool 94% utilized: prod-pg-01',     severity: 'WARNING',  status: 'OPEN',         source: 'DATABASE', triggeredAt: ago(7)   },
    { _id: 'inf3',  title: 'Pub/Sub backlog > 100k messages: orders-sub',  severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(14)  },
    { _id: 'inf4',  title: 'Azure AKS node memory pressure: 87%',          severity: 'WARNING',  status: 'ACKNOWLEDGED', source: 'AZURE',    triggeredAt: ago(22)  },
    { _id: 'inf5',  title: 'MongoDB slow query > 2s: prod.events',         severity: 'INFO',     status: 'OPEN',         source: 'MONGODB',  triggeredAt: ago(31)  },
    { _id: 'inf6',  title: 'GKE disk pressure: node gke-prod-node-3 91%',  severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(47)  },
  ],

  apm: [
    { _id: 'apm1',  title: 'Error rate spike: api-gateway 18.4% (SLO breach)',    severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(2)   },
    { _id: 'apm2',  title: 'P99 latency 3.2s: checkout-service (SLA > 500ms)',    severity: 'CRITICAL', status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(5)   },
    { _id: 'apm3',  title: 'Circuit breaker OPEN: payment-service (5xx > 40%)',   severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(9)   },
    { _id: 'apm4',  title: 'JVM heap 92% used: order-processor (GC pressure)',    severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(13)  },
    { _id: 'apm5',  title: 'HTTP 5xx rate > 8%: user-service (rolling 5m)',       severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(17)  },
    { _id: 'apm6',  title: 'DB connection pool saturated 98%: prod-pg-01',        severity: 'WARNING',  status: 'ACKNOWLEDGED', source: 'DATABASE', triggeredAt: ago(21)  },
    { _id: 'apm7',  title: 'Kafka consumer lag 87k messages: events-topic',       severity: 'WARNING',  status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(28)  },
    { _id: 'apm8',  title: 'Redis cache hit rate dropped to 34% (normal: 85%)',   severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(34)  },
    { _id: 'apm9',  title: 'Slow query 6.1s: SELECT orders JOIN users (seq scan)',severity: 'WARNING',  status: 'OPEN',         source: 'DATABASE', triggeredAt: ago(41)  },
    { _id: 'apm10', title: 'Memory leak: background-worker RSS +1.2GB/hr',        severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(48)  },
    { _id: 'apm11', title: 'API response time SLO burn rate: 14x budget',         severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(54)  },
    { _id: 'apm12', title: 'CosmosDB RU throttling: read-api 429 responses',      severity: 'WARNING',  status: 'ACKNOWLEDGED', source: 'AZURE',    triggeredAt: ago(62)  },
    { _id: 'apm13', title: 'Thread pool exhaustion: async-processor (400 threads)',severity: 'WARNING', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(71)  },
    { _id: 'apm14', title: 'Auto-scale limit reached: api-gateway maxed at 20',   severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(84)  },
    { _id: 'apm15', title: 'gRPC deadline exceeded: inventory-service (p95 4.1s)',severity: 'WARNING',  status: 'ACKNOWLEDGED', source: 'GCP',      triggeredAt: ago(97)  },
    { _id: 'apm16', title: 'Disk I/O saturation: prod-pg-01 await > 120ms',       severity: 'INFO',     status: 'OPEN',         source: 'DATABASE', triggeredAt: ago(112) },
    { _id: 'apm17', title: 'Azure SQL DTU 91% utilized: prod-db-eastus2',         severity: 'WARNING',  status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(128) },
  ],

  security: [
    { _id: 'sec1',  title: 'Brute force: 203.0.113.42 — 89 failures in 2min',         severity: 'CRITICAL', status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(1)   },
    { _id: 'sec2',  title: 'Privilege escalation: svc-account-3 accessed /admin/iam', severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(4)   },
    { _id: 'sec3',  title: 'SQL injection blocked: POST /api/search (WAF rule 942)',   severity: 'CRITICAL', status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(7)   },
    { _id: 'sec4',  title: 'Data exfiltration alert: 4.7GB egress to 198.51.100.9',   severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(11)  },
    { _id: 'sec5',  title: 'Zero-day CVE-2024-3094 exploit attempt detected',         severity: 'CRITICAL', status: 'ACKNOWLEDGED', source: 'GCP',      triggeredAt: ago(16)  },
    { _id: 'sec6',  title: 'Unauthorized DELETE /admin/users/u_9912 — RBAC violation',severity: 'CRITICAL', status: 'ACKNOWLEDGED', source: 'GCP',      triggeredAt: ago(22)  },
    { _id: 'sec7',  title: 'TLS 1.0 handshake detected: legacy client 10.1.2.88',     severity: 'WARNING',  status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(28)  },
    { _id: 'sec8',  title: 'Suspicious geo-login: user u_9821 from CN (usual: US)',   severity: 'WARNING',  status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(34)  },
    { _id: 'sec9',  title: 'API key leaked in public GitHub repo — key rotated',       severity: 'CRITICAL', status: 'ACKNOWLEDGED', source: 'GCP',      triggeredAt: ago(39)  },
    { _id: 'sec10', title: 'Port scan detected: 10.0.44.22 probing 1800 ports/min',   severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(46)  },
    { _id: 'sec11', title: 'SSL certificate expires in 6 days: api.prod.example.com', severity: 'WARNING',  status: 'OPEN',         source: 'DATABASE', triggeredAt: ago(54)  },
    { _id: 'sec12', title: 'GDPR: PII accessed outside EU region — compliance flag',  severity: 'CRITICAL', status: 'OPEN',         source: 'MONGODB',  triggeredAt: ago(61)  },
    { _id: 'sec13', title: 'XSS payload detected: GET /profile?name=<script>…',       severity: 'WARNING',  status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(69)  },
    { _id: 'sec14', title: 'Container escape attempt: privileged pod gke-prod-7',     severity: 'CRITICAL', status: 'OPEN',         source: 'GCP',      triggeredAt: ago(77)  },
    { _id: 'sec15', title: 'SOC 2 control failure: MFA not enforced for 3 accounts',  severity: 'WARNING',  status: 'ACKNOWLEDGED', source: 'AZURE',    triggeredAt: ago(85)  },
    { _id: 'sec16', title: 'Credential stuffing: 312 accounts tested from 5 IPs',     severity: 'CRITICAL', status: 'OPEN',         source: 'AZURE',    triggeredAt: ago(93)  },
    { _id: 'sec17', title: 'SSRF attempt: internal metadata endpoint probed',         severity: 'WARNING',  status: 'OPEN',         source: 'GCP',      triggeredAt: ago(102) },
    { _id: 'sec18', title: 'pg_hba.conf auth failure: unknown host 172.16.8.44',      severity: 'WARNING',  status: 'OPEN',         source: 'DATABASE', triggeredAt: ago(118) },
  ],
};

// ─── Demo logs per template ───────────────────────────────────────────────────
let _dlIdx = 0;
const mkLog = (src, lvl, svc, msg, msAgo) => ({
  _id: `dl-${++_dlIdx}-${src}`,
  source: src, level: lvl, service: svc, message: msg,
  timestamp: new Date(Date.now() - msAgo).toISOString(),
});

const DEMO_LOGS = {
  infrastructure: [
    mkLog('GCP',      'ERROR',    'cloud-run/api-gateway',   'Cloud Run OOM kill — memory limit 512Mi exceeded, instance restarted',       5000),
    mkLog('AZURE',    'WARN',     'AKS/nodepool1',           'AKS node memory pressure: aks-nodepool1-12345 at 87% — eviction pending',    12000),
    mkLog('MONGODB',  'WARN',     'mongodb/prod.events',     'Slow query 847ms: db.events.find({user_id:"u_4421"}) — missing index hint',  19000),
    mkLog('DATABASE', 'ERROR',    'postgresql/slow-queries', 'Deadlock detected: pid=28471 waits ShareLock on transaction 8291',           28000),
    mkLog('GCP',      'INFO',     'cloud-run/auth-service',  'Auto-scaling triggered: replicas 3 → 8 (cpu_utilization=78%)',               37000),
    mkLog('AZURE',    'INFO',     'AppService/api',          'App Service plan auto-scale out: 3 → 6 instances triggered',                46000),
    mkLog('MONGODB',  'INFO',     'mongodb/prod.orders',     'Index build complete: idx_events_user_ts — 2.4M docs in 18.3s',             55000),
    mkLog('DATABASE', 'WARN',     'postgresql/connections',  'Connection pool 78% utilized: 156/200 connections active',                   64000),
    mkLog('GCP',      'DEBUG',    'bigquery/analytics',      'Health check passed: GET /api/v2/health → 200 OK (34ms)',                    73000),
    mkLog('AZURE',    'ERROR',    'KeyVault/prod-kv',        'Key Vault secret rotation failed: api-key-stripe — RBAC denied',             82000),
    mkLog('GCP',      'WARN',     'gke/backend',             'GKE node disk pressure: gke-prod-node-3 at 91% — pod eviction triggered',   91000),
    mkLog('DATABASE', 'INFO',     'postgresql/prod-pg-01',   'VACUUM ANALYZE complete: prod.events — 2.1M rows, 847 dead tuples removed', 103000),
  ],

  apm: [
    mkLog('GCP',      'ERROR',    'cloud-run/api-gateway',    'P99 latency 3.2s: POST /api/v2/checkout — downstream payment timeout',       2000),
    mkLog('GCP',      'ERROR',    'cloud-run/payment-svc',    'HTTP 503 ServiceUnavailable: payment-service unreachable after 3 retries',    5000),
    mkLog('GCP',      'CRITICAL', 'cloud-run/order-proc',     'Circuit breaker OPEN: inventory-service 5xx rate 43% over last 60s',         8000),
    mkLog('AZURE',    'ERROR',    'AppService/api',            'Unhandled exception: NullPointerException at OrderProcessor.java:142',       12000),
    mkLog('GCP',      'ERROR',    'cloud-run/api-gateway',    'SLO burn rate 14x: api-gateway error budget exhausted at current rate',       16000),
    mkLog('DATABASE', 'ERROR',    'postgresql/slow-queries',  'Slow query 6.1s: SELECT o.* FROM orders o JOIN users u ON … (seq scan)',     20000),
    mkLog('GCP',      'WARN',     'cloud-run/api-gateway',    'Rate limit 80% hit: client 10.0.2.15 at 1,200 req/min (limit 1,500)',        24000),
    mkLog('GCP',      'WARN',     'cloud-run/order-proc',     'JVM heap 92%: GC overhead 28% — old-gen collection every 3s',               28000),
    mkLog('AZURE',    'WARN',     'AzureSQL/prod-db',         'Azure SQL DTU 91% utilized: prod-db-eastus2 — query queuing detected',       33000),
    mkLog('GCP',      'WARN',     'pubsub/events-topic',      'Kafka consumer lag 87k messages: events-topic group analytics-consumer',     38000),
    mkLog('MONGODB',  'WARN',     'mongodb/prod.events',      'Write concern timeout {w:"majority"} after 5000ms — replica sync lag',       43000),
    mkLog('GCP',      'ERROR',    'cloud-run/auth-service',   'JWT validation failed: token expired for user u_89234 — 401 Unauthorized',   48000),
    mkLog('DATABASE', 'WARN',     'postgresql/locks',         'Lock wait 7s: pid 28471 blocked by pid 28450 on UPDATE orders SET status',   54000),
    mkLog('GCP',      'WARN',     'cloud-run/cache-svc',      'Redis cache hit rate 34% (baseline 85%) — cold cache after restart',         60000),
    mkLog('GCP',      'INFO',     'cloud-run/api-gateway',    'Deployment rollout complete: api-gateway v2.14.3 — 0 errors during rollout', 67000),
    mkLog('AZURE',    'ERROR',    'EventHub/orders',           'Event Hub consumer group lag: 51k messages unprocessed — partition 3',       73000),
    mkLog('GCP',      'WARN',     'cloud-run/background-wkr', 'Memory leak suspected: RSS growing +1.2GB/hr — heap dump triggered',        79000),
    mkLog('DATABASE', 'WARN',     'postgresql/prod-pg-01',    'Disk I/O saturation: await 142ms, util 97% — query slowdown expected',       86000),
    mkLog('AZURE',    'WARN',     'CosmosDB/userdata',        'CosmosDB 429 throttling: 312 req/s exceeds provisioned 250 RU/s',            93000),
    mkLog('GCP',      'INFO',     'pubsub/orders-sub',        'Pub/Sub backlog cleared: 45k → 0 messages processed in 12.3s',              101000),
    mkLog('GCP',      'WARN',     'cloud-run/user-svc',       'Thread pool near-saturation: 380/400 threads active — queue depth 1,200',   109000),
    mkLog('AZURE',    'DEBUG',    'AzureSQL/prod-db',         'Query plan recompile forced: parameter sniffing detected on GetUserOrders',  118000),
    mkLog('GCP',      'INFO',     'cloud-run/api-gateway',    'Auto-scale triggered: api-gateway 8 → 14 instances (cpu_util 82%)',          128000),
    mkLog('DATABASE', 'INFO',     'postgresql/prod-pg-01',    'pg_stat_statements reset: 48h query stats cleared by scheduled maintenance', 138000),
    mkLog('GCP',      'DEBUG',    'cloud-run/payment-svc',    'gRPC health check OK: inventory-service responded in 18ms',                 148000),
  ],

  security: [
    mkLog('AZURE',    'CRITICAL', 'AAD/SignIn',               'Brute force: 89 failed logins from 203.0.113.42 in 2min — IP auto-blocked',  1000),
    mkLog('GCP',      'CRITICAL', 'cloud-run/auth-service',   'Privilege escalation: svc-account-3 invoked /admin/iam without RBAC role',   4000),
    mkLog('AZURE',    'CRITICAL', 'AppService/WAF',           'SQL injection blocked: POST /api/search — WAF rule 942100 triggered',        7000),
    mkLog('GCP',      'CRITICAL', 'gke/network-policy',       'Data exfiltration alert: 4.7GB outbound to 198.51.100.9 (not in allowlist)', 11000),
    mkLog('GCP',      'ERROR',    'cloud-run/api-gateway',    'Unauthorized DELETE /admin/users/u_9912 — 403 Forbidden, event logged',      15000),
    mkLog('AZURE',    'CRITICAL', 'KeyVault/prod-kv',         'Secret rotation failure: stripe-api-key — service principal RBAC denied',    19000),
    mkLog('GCP',      'CRITICAL', 'cloud-run/auth-service',   'Token replay attack: JWT jti "f7d3c1" already consumed — request rejected',  23000),
    mkLog('AZURE',    'CRITICAL', 'AAD/Audit',                'Credential stuffing: 312 accounts tested in 4min from rotating IPs',         27000),
    mkLog('GCP',      'WARN',     'gke/pod-security',         'Container escape attempt: privileged pod gke-prod-7 requested host PID ns',  31000),
    mkLog('AZURE',    'WARN',     'AAD/SignIn',               'Geo-anomaly: user u_9821 signed in from CN — baseline country US, flagged',   36000),
    mkLog('GCP',      'ERROR',    'cloud-run/api-gateway',    'XSS payload in query param: GET /profile?name=<script>alert(1)</script>',    41000),
    mkLog('DATABASE', 'WARN',     'postgresql/audit',         'pg_hba.conf auth failure: host 172.16.8.44 not in pg_hba — connection reset', 46000),
    mkLog('MONGODB',  'ERROR',    'mongodb/prod.users',       'Schema violation: field "admin_override:true" in insert — write blocked',     51000),
    mkLog('GCP',      'WARN',     'gke/network-policy',       'Network policy violation: pod analytics → prod-db:5432 (blocked rule 44)',   56000),
    mkLog('AZURE',    'WARN',     'AzureMonitor',             'TLS 1.0 handshake from 10.1.2.88 — deprecated protocol, client notified',    62000),
    mkLog('GCP',      'WARN',     'cloud-run/api-gateway',    'SSRF probe: request to 169.254.169.254 (metadata) blocked by egress rule',   68000),
    mkLog('GCP',      'ERROR',    'cloud-run/auth-service',   'Session hijacking attempt: cookie reuse across 3 IPs for session s_7821',    74000),
    mkLog('AZURE',    'CRITICAL', 'AAD/Audit',                'Impossible travel: user u_4412 authenticated from NY then SG within 4min',   80000),
    mkLog('DATABASE', 'WARN',     'postgresql/audit',         'Row-level security bypass attempt on table billing.invoices — blocked',       86000),
    mkLog('GCP',      'INFO',     'cloud-run/auth-service',   'Emergency MFA reset approved for user u_7712 by admin — audit logged',       92000),
    mkLog('AZURE',    'WARN',     'KeyVault/prod-kv',         'Certificate api.prod.example.com expires in 6 days — renewal pending',       99000),
    mkLog('MONGODB',  'WARN',     'mongodb/prod.payments',    'GDPR flag: payment PII read from US-west node — policy requires EU-only',   106000),
    mkLog('GCP',      'ERROR',    'gke/backend-deployment',   'CVE-2024-3094 exploit signature matched in request body — dropped',         113000),
    mkLog('AZURE',    'INFO',     'AzureMonitor',             'Conditional access policy updated: MFA now required for all admin roles',    120000),
    mkLog('GCP',      'WARN',     'cloud-run/api-gateway',    'API key exposed in GitHub commit sha:4a7c3d — key rotated, user notified',  128000),
    mkLog('DATABASE', 'INFO',     'postgresql/audit',         'SOC 2 audit log exported: 30-day access records for compliance review',     136000),
  ],
};

// ─── Main Dashboard component ─────────────────────────────────────────────────
export default function Dashboard() {
  const {
    alerts, setAlerts,
    logs, addLogs,
    timeRange, autoRefresh,
    variables,
    annotations, setAnnotations,
    showAnnotations,
    setAnalytics,
    activeTemplate,
  } = useStore();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const criticals = alerts.filter(a => a.severity === 'CRITICAL' && a.status === 'OPEN').length;
  const openAlerts = alerts.filter(a => a.status === 'OPEN').length;

  // ── Fetch data ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sinceMinutes: PRESET_MINUTES[timeRange] || 60 };
      if (variables.source   !== 'ALL') params.source   = variables.source;
      if (variables.severity !== 'ALL') params.severity = variables.severity;

      const [overview] = await Promise.all([
        analyticsApi.overview(params),
      ]);

      const processed = processApiData(overview, timeRange, variables);

      // If the selected source/severity combo returned no events, use demo data
      // (e.g. AZURE selected but no AZURE logs ingested yet)
      const hasRealData = processed.eventsPerMin > 0 || processed.volumeTrend.length > 0;
      if (!hasRealData) {
        setData(buildDemoData(timeRange, variables, activeTemplate));
        if (alerts.length === 0) setAlerts(DEMO_ALERTS[activeTemplate] || DEMO_ALERTS.infrastructure);
        if (logs.length === 0)   addLogs(DEMO_LOGS[activeTemplate]   || DEMO_LOGS.infrastructure);
      } else {
        setData(processed);
      }
      setAnalytics(overview);

      // Load annotations for the time window
      try {
        const mins  = PRESET_MINUTES[timeRange] || 60;
        const from  = new Date(Date.now() - mins * 60000).toISOString();
        const anns  = await annotationsApi.list({ from, limit: 50 });
        setAnnotations(anns);
      } catch { /* annotations are optional */ }

    } catch {
      setData(buildDemoData(timeRange, variables, activeTemplate));
      // Seed demo content when store is empty (backend unavailable)
      if (alerts.length === 0) {
        setAlerts(DEMO_ALERTS[activeTemplate] || DEMO_ALERTS.infrastructure);
      }
      if (logs.length === 0) {
        addLogs(DEMO_LOGS[activeTemplate] || DEMO_LOGS.infrastructure);
      }
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [timeRange, variables, activeTemplate, setAnalytics, setAnnotations]);

  // ── Initial + time-range/variable change ───────────────────────────────────
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Auto-refresh ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh > 0) {
      timerRef.current = setInterval(fetchData, autoRefresh * 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, fetchData]);

  // ── Datasource label: reflects active source filter ────────────────────────
  const dsLabel = variables.source === 'ALL' ? 'All Sources' : variables.source;

  // ── Stat panel config ──────────────────────────────────────────────────────
  const statPanels = data ? [
    {
      title:       'Error Rate',
      value:       data.errorRate,
      unit:        '%',
      color:       data.errorRate > 10 ? '#ef4444' : data.errorRate > 5 ? '#f59e0b' : '#22c55e',
      icon:        AlertTriangle,
      trendPct:    0.8,
      trendLabel:  'vs prev period',
      sparkline:   data.errorSparkline,
      description: 'ERROR + CRITICAL / total',
      datasource:  dsLabel,
    },
    {
      title:       'Events / min',
      value:       data.eventsPerMin.toLocaleString(),
      unit:        '',
      color:       '#3b82f6',
      icon:        Activity,
      trendPct:    -1.2,
      trendLabel:  'vs prev period',
      sparkline:   data.eventsSparkline,
      description: `Over last ${timeRange}`,
      datasource:  dsLabel,
    },
    {
      title:       'Active Alerts',
      value:       openAlerts,
      unit:        '',
      color:       criticals > 0 ? '#ef4444' : openAlerts > 0 ? '#f59e0b' : '#22c55e',
      icon:        Zap,
      trendPct:    criticals > 0 ? criticals : 0,
      trendLabel:  criticals > 0 ? 'critical' : 'all healthy',
      description: `${criticals} critical`,
      datasource:  dsLabel,
    },
    {
      title:       'P95 Latency',
      value:       data.p95Latency,
      unit:        'ms',
      color:       data.p95Latency > 300 ? '#ef4444' : data.p95Latency > 150 ? '#f59e0b' : '#22c55e',
      icon:        Clock,
      trendPct:    2.1,
      trendLabel:  'vs avg',
      sparkline:   data.latencySparkline,
      description: '95th percentile',
      datasource:  dsLabel,
    },
    {
      title:       'Throughput',
      value:       data.throughput,
      unit:        '/s',
      color:       '#8b5cf6',
      icon:        TrendingUp,
      trendPct:    -0.5,
      trendLabel:  'vs prev',
      description: 'Requests per second',
      datasource:  dsLabel,
    },
    {
      title:       'Uptime',
      value:       data.uptime,
      unit:        '%',
      color:       '#22c55e',
      icon:        CheckCircle,
      description: variables.source === 'ALL' ? 'All sources' : variables.source,
      datasource:  dsLabel,
    },
  ] : [];

  // ── Gauge thresholds ───────────────────────────────────────────────────────
  const errorGaugeThresholds = [
    { at: 5,   color: '#22c55e' },
    { at: 15,  color: '#f59e0b' },
    { at: 100, color: '#ef4444' },
  ];
  const latencyThresholds = [
    { at: 150,  color: '#22c55e' },
    { at: 300,  color: '#f59e0b' },
    { at: 1000, color: '#ef4444' },
  ];

  // ── Shared panel props ─────────────────────────────────────────────────────
  const timeSeriesProps = {
    data: data?.volumeTrend || [],
    annotations, showAnnotations,
    loading: loading && !data,
    title: 'Log Volume Trend',
    subtitle: `Last ${timeRange} · 12 buckets`,
  };
  const pieProps = {
    data: data?.alertsBySeverity || [],
    loading: loading && !data,
    title: 'Alert Severity Distribution',
  };
  const barProps = {
    data: data?.sourceBreakdown || [],
    loading: loading && !data,
    title: 'Log Volume by Source',
  };
  const heatmapProps = {
    data: data?.heatmap,
    loading: loading && !data,
    title: 'Error Heatmap',
    subtitle: 'Errors per source × time bucket',
  };
  const errorGaugeProps = {
    title: 'Error Rate', value: data?.errorRate ?? 0,
    min: 0, max: 30, unit: '%',
    thresholds: errorGaugeThresholds,
    loading: loading && !data, datasource: dsLabel,
  };
  const latencyGaugeProps = {
    title: 'P95 Latency', value: data?.p95Latency ?? 0,
    min: 0, max: 500, unit: 'ms',
    thresholds: latencyThresholds,
    loading: loading && !data, datasource: dsLabel,
  };

  function renderToolbar() {
    return (
      <>
        <DashboardToolbar onRefresh={fetchData} loading={loading} />
        {lastRefresh && (
          <div style={{
            fontSize: 10, color: 'var(--text-3)',
            padding: '4px 16px 0', textAlign: 'right',
          }}>
            Last refresh: {lastRefresh.toLocaleTimeString()}
            {autoRefresh > 0 && ` · auto every ${autoRefresh}s`}
          </div>
        )}
      </>
    );
  }

  // ── APM template ───────────────────────────────────────────────────────────
  if (activeTemplate === 'apm') {
    const apmStats = data ? [statPanels[0], statPanels[3], statPanels[4], statPanels[1]] : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {renderToolbar()}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {/* 4 KPI stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
            {apmStats.map(p => p && <StatPanel key={p.title} {...p} />)}
          </div>
          {/* Full-width time series */}
          <div style={{ marginBottom: 8 }}>
            <TimeSeriesPanel {...timeSeriesProps} subtitle={`Last ${timeRange} — Error Rate & Throughput`} />
          </div>
          {/* Bar (3/4) + Pie (1/4) */}
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 8, marginBottom: 8 }}>
            <BarPanel {...barProps} title="Events by Source" />
            <PiePanel {...pieProps} />
          </div>
          {/* Full-width logs */}
          <div style={{ marginBottom: 8 }}>
            <LogsPanel loading={loading && !data} />
          </div>
          {/* Full-width alerts */}
          <AlertsPanel loading={loading && !data} />
        </div>
      </div>
    );
  }

  // ── Security template ──────────────────────────────────────────────────────
  if (activeTemplate === 'security') {
    const secStats = data ? [
      {
        title: 'Active Alerts', value: openAlerts, unit: '',
        color: criticals > 0 ? '#ef4444' : openAlerts > 0 ? '#f59e0b' : '#22c55e',
        icon: Zap, description: 'Open incidents', datasource: dsLabel,
      },
      {
        title: 'Critical', value: criticals, unit: '',
        color: criticals > 0 ? '#ef4444' : '#22c55e',
        icon: AlertTriangle, description: 'CRITICAL severity', datasource: dsLabel,
      },
      statPanels[0],  // Error Rate  (already uses dsLabel)
      statPanels[5],  // Uptime      (already uses dsLabel)
    ] : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {renderToolbar()}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {/* 4 KPI stats (security-focused) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
            {secStats.map(p => p && <StatPanel key={p.title} {...p} />)}
          </div>
          {/* Full-width alerts table */}
          <div style={{ marginBottom: 8 }}>
            <AlertsPanel loading={loading && !data} />
          </div>
          {/* Error gauge (1/4) + Pie (3/4) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 8, marginBottom: 8 }}>
            <GaugePanel {...errorGaugeProps} title="Error Rate" />
            <PiePanel {...pieProps} title="Alert Severity Breakdown" />
          </div>
          {/* Heatmap */}
          <div style={{ marginBottom: 8 }}>
            <HeatmapPanel {...heatmapProps} title="Security Event Heatmap" subtitle="Error events per source × time" />
          </div>
          {/* Full-width logs */}
          <LogsPanel loading={loading && !data} />
        </div>
      </div>
    );
  }

  // ── Infrastructure template (default) ─────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {renderToolbar()}

      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>

        {/* ── Row 1: KPI Stats ────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 8 }}>
          {statPanels.map(p => (
            <StatPanel key={p.title} {...p} />
          ))}
        </div>

        {/* ── Row 2: Time Series (wide) + Alert Pie (narrow) ──────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 8, marginBottom: 8 }}>
          <TimeSeriesPanel {...timeSeriesProps} />
          <PiePanel {...pieProps} />
        </div>

        {/* ── Row 3: Gauges + Bar chart ────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 8, marginBottom: 8 }}>
          <GaugePanel {...errorGaugeProps} />
          <GaugePanel {...latencyGaugeProps} />
          <BarPanel {...barProps} />
        </div>

        {/* ── Row 4: Heatmap ───────────────────────────────────────────────── */}
        <div style={{ marginBottom: 8 }}>
          <HeatmapPanel {...heatmapProps} />
        </div>

        {/* ── Row 5: Logs + Alerts table ───────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <LogsPanel loading={loading && !data} />
          <AlertsPanel loading={loading && !data} />
        </div>

      </div>
    </div>
  );
}
